import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BrowserProvider } from 'ethers';
import { useAuth } from '../context/AuthContext.js';
import { getTokenIntel, webScan, getWebNonce, verifyWebSignature } from '../services/api.js';
import {
  SearchIcon,
} from '../components/Icons.js';
import { LEGAL_DISCLAIMER } from '../data/metric-explanations.js';
import {
  ConfidencePanel,
  VerdictExplanation,
  RiskBreakdown,
  EliteIntelPanel,
  AdvancedForensics,
  ProInvestorSummary,
  ProCoreMetrics,
  EliteSectionDivider,
} from '../components/intel/index.js';
import { RISK_LABELS } from '../lib/riskLabels.js';
import { formatSafetyScore } from '../lib/scoreFormatter.js';
import { normalizeIntel } from '../utils/intelNormalizer.js';
import { buildCoreMetrics, buildRiskDomains } from '../lib/coreMetricsBuilder.js';

type RiskLevel = 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN';

const WEB_TOKEN_KEY = 'web_auth_token';
const WEB_WALLET_KEY = 'web_wallet_address';

const SUPPORTED_CHAINS = ['eth', 'sol', 'base', 'bnb', 'avax'] as const;

const CHAIN_LABELS: Record<string, string> = {
  eth: 'Ethereum',
  sol: 'Solana',
  base: 'Base',
  bnb: 'BNB Chain',
  avax: 'Avalanche',
};

const LOCKED_SECTIONS = [
  { id: 'contractSafety', label: 'Sellability Check', icon: 'shield' },
  { id: 'liquidity', label: 'Liquidity Analysis', icon: 'chart' },
  { id: 'holders', label: 'Holder Risk Analysis', icon: 'users' },
  { id: 'smartMoney', label: 'Smart Money Signals', icon: 'brain' },
  { id: 'social', label: 'Social Intelligence', icon: 'globe' },
  { id: 'network', label: 'Network Graph', icon: 'network' },
];

const RISK_COLORS: Record<string, string> = {
  GREEN: '#27ae60',
  YELLOW: '#f39c12',
  RED: '#e74c3c',
  UNKNOWN: '#95a5a6',
};

const RISK_ICONS_MAP: Record<string, string> = {
  GREEN: 'shield',
  YELLOW: 'alert',
  RED: 'alert',
  UNKNOWN: 'alert',
};

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function truncAddr(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function fmtUsd(val: number | null | undefined): string {
  if (val == null) return 'No data available yet';
  return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export function WebSearch() {
  const { chain: paramChain, token: paramToken } = useParams<{ chain: string; token: string }>();
  const navigate = useNavigate();
  const { walletAddress } = useAuth();

  const [chain, setChain] = useState(paramChain || 'eth');
  const [address, setAddress] = useState(paramToken || '');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webToken, setWebToken] = useState<string | null>(() => localStorage.getItem(WEB_TOKEN_KEY));

  // Web auth: auto-authenticate for scanner JWT when wallet is connected
  const authenticateWeb = useCallback(async () => {
    if (!walletAddress || !window.ethereum) return;
    try {
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const { nonce } = await getWebNonce(walletAddress);
      const signature = await signer.signMessage(nonce);
      const authResult = await verifyWebSignature(walletAddress, signature, nonce);
      localStorage.setItem(WEB_TOKEN_KEY, authResult.token);
      localStorage.setItem(WEB_WALLET_KEY, authResult.walletAddress);
      setWebToken(authResult.token);
    } catch {
      // Non-critical — scanner will use public endpoint
    }
  }, [walletAddress]);

  useEffect(() => {
    if (walletAddress && !webToken) {
      authenticateWeb();
    }
  }, [walletAddress, webToken, authenticateWeb]);

  const runScan = useCallback(async (scanChain: string, scanToken: string) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const currentToken = localStorage.getItem(WEB_TOKEN_KEY);
      let data: any;

      if (currentToken) {
        // Authenticated: use metered scan (records history + increments usage)
        try {
          data = await webScan(currentToken, scanChain, scanToken);
        } catch (meteredErr: any) {
          if (meteredErr.statusCode === 401) {
            localStorage.removeItem(WEB_TOKEN_KEY);
            localStorage.removeItem(WEB_WALLET_KEY);
            setWebToken(null);
          }
          if (meteredErr.statusCode === 403 || meteredErr.code === 'SCAN_LIMIT_REACHED') {
            setError(meteredErr.message || 'Scan limit reached for this billing period');
            return;
          }
          // Fall back to public endpoint only on non-auth errors
          data = await getTokenIntel(scanChain, scanToken);
        }
      } else {
        // Anonymous: use public preview endpoint
        data = await getTokenIntel(scanChain, scanToken);
      }

      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Scan failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (paramChain && paramToken) {
      setChain(paramChain);
      setAddress(paramToken);
      runScan(paramChain, paramToken);
    }
  }, [paramChain, paramToken, runScan]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = address.trim();
    if (!trimmed) return;
    // If URL already matches (re-scan same token), call runScan directly
    // since navigate() to the same URL won't trigger the useEffect
    if (paramChain === chain && paramToken === trimmed) {
      runScan(chain, trimmed);
    } else {
      navigate(`/search/${chain}/${trimmed}`);
    }
  };

  // Deep-tier result resolution
  const isDeep = result?.tier === 'deep';
  const d = isDeep ? result.data : result;
  const riskLevel: RiskLevel = d?.riskLevel || result?.riskLevel || 'GREEN';
  const score = d?.score ?? result?.score ?? 0;
  const headline = d?.headline || result?.headline;
  const project = d?.project || result?.project;
  const meta = result?.meta ?? { cached: false, latencyMs: 0, source: 'unknown' };

  // Normalize deep data for v3 components (same as TokenIntel.tsx)
  const intel = useMemo(() => {
    if (!isDeep || !d) return null;
    return normalizeIntel(d as Record<string, unknown>);
  }, [isDeep, d]);

  const riskColor = RISK_COLORS[riskLevel] ?? RISK_COLORS.UNKNOWN;

  return (
    <div className="ws-page">
      <div className="ws-hero">
        <h1 className="ws-title">CryptoGuardians Token Scanner</h1>
        <p className="ws-subtitle">Scan any token contract for threats, scams, and risk signals.</p>
      </div>

      <form className="ws-search-header" onSubmit={handleSubmit}>
        <select
          className="ws-chain-select"
          value={chain}
          onChange={(e) => setChain(e.target.value)}
          aria-label="Select chain"
        >
          {SUPPORTED_CHAINS.map((c) => (
            <option key={c} value={c}>{CHAIN_LABELS[c]}</option>
          ))}
        </select>
        <input
          className="ws-search-input"
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Enter token contract address"
          aria-label="Token contract address"
        />
        <button className="ws-scan-btn" type="submit" disabled={loading || !address.trim()}>
          {loading ? 'Scanning...' : 'Scan'}
        </button>
      </form>

      {loading && (
        <div className="ws-result-card ws-loading">
          <div className="ws-skeleton" />
          <div className="ws-skeleton ws-skeleton-short" />
          <div className="ws-skeleton ws-skeleton-short" />
        </div>
      )}

      {error && (
        <div className="ws-result-card ws-error-card">
          <p>{error}</p>
          <button className="ws-scan-btn" onClick={() => paramChain && paramToken && runScan(paramChain, paramToken)}>
            Retry
          </button>
        </div>
      )}

      {result && !loading && (
        <>
          {/* ---- Token Header ---- */}
          <div className="ws-result-card">
            <div className="ws-result-header">
              <span className={`ws-risk-badge ws-risk-${riskLevel}`}>
                {RISK_LABELS[riskLevel] || riskLevel}
              </span>
              {isDeep && <span className="ws-tier-badge-pro">Pro Intel</span>}
              {project?.name && (
                <span className="ws-token-name">
                  {project.name}
                  {project.ticker ? ` (${project.ticker})` : ''}
                </span>
              )}
            </div>

            {/* ---- Risk Banner ---- */}
            {headline && (
              <h2 className="ws-headline">{headline}</h2>
            )}

            <div className="ws-metrics-row">
              <div className="ws-metric">
                <span className="ws-metric-label">Risk Score</span>
                <span className="ws-metric-value" style={{ color: riskColor }}>
                  {formatSafetyScore(score)?.toFixed(1) ?? '0.0'} / 10
                </span>
                <div className="ws-score-bar">
                  <div
                    className="ws-score-fill"
                    style={{ width: `${Math.min(100, (formatSafetyScore(score) ?? 0) * 10)}%`, backgroundColor: riskColor }}
                  />
                </div>
              </div>
            </div>
            <p className="intel-risk-disclaimer" style={{ margin: '0.5rem 0 0', fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.4 }}>
              This assessment reflects automated risk indicators, does not eliminate risk, and should not be interpreted as assurance.
            </p>

            {/* ---- Project Overview ---- */}
            <div className="intel-grid intel-grid-4" style={{ marginTop: '1rem' }}>
              <div className="intel-metric-card card">
                <div className="intel-metric">
                  <span className="intel-metric-label">Name</span>
                  <span className="intel-metric-value">{intel?.tokenName ?? project?.name ?? 'Unknown'}</span>
                </div>
              </div>
              <div className="intel-metric-card card">
                <div className="intel-metric">
                  <span className="intel-metric-label">Ticker</span>
                  <span className="intel-metric-value">{intel?.tokenSymbol ?? project?.ticker ?? '\u2014'}</span>
                </div>
              </div>
              <div className="intel-metric-card card">
                <div className="intel-metric">
                  <span className="intel-metric-label">Deployer</span>
                  <span className="intel-metric-value">
                    {project?.deployer ? <code>{truncAddr(project.deployer)}</code> : 'Unknown'}
                  </span>
                </div>
              </div>
              <div className="intel-metric-card card">
                <div className="intel-metric">
                  <span className="intel-metric-label">Liquidity</span>
                  <span className="intel-metric-value">{fmtUsd(project?.liquidityUsd)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ---- Deep sections (pro only) — v3 Educational + Pro + Elite layout ---- */}
          {intel && (
            <>
              {/* ===== SECTION 1: INVESTOR SUMMARY ===== */}
              <ProInvestorSummary
                score={formatSafetyScore(score) ?? 0}
                riskLevel={riskLevel}
                confidence={intel.analysisConfidence || undefined}
                confidenceLevel={intel.analysisConfidenceLevel || undefined}
                verifiedSafe={intel.verifiedSafe || undefined}
                verifiedSafeSource={intel.verifiedSafeSource || undefined}
                summary={intel.verdictSummary || undefined}
              />

              {/* ===== SECTION 2: CORE SAFETY METRICS ===== */}
              <ProCoreMetrics metrics={buildCoreMetrics(intel)} />

              {/* ===== SECTION 3: WHY THIS VERDICT ===== */}
              <VerdictExplanation
                risks={intel.risks}
                positives={intel.positives}
                signals={intel.signals}
                riskLevel={riskLevel}
              />

              {/* ===== ELITE DIVIDER ===== */}
              <EliteSectionDivider isEliteTier={false} />

              {/* ===== SECTION 4: RISK BREAKDOWN — per-domain grid ===== */}
              <RiskBreakdown domains={buildRiskDomains(intel)} />

              {/* ===== SECTION 5: FORENSIC INTELLIGENCE PANELS ===== */}
              <EliteIntelPanel
                walletIntel={intel.creatorRisk}
                fundingAttribution={intel.creatorFunding}
                contractIntel={intel.contractSafety}
                approvalIntel={intel.contractApproval}
                liquidityIntel={intel.liquiditySafety}
                lockerIntel={intel.liquidityLocker}
                holderIntel={intel.holderRisk}
                socialIntel={intel.socialTrust}
              />

              {/* ===== SECTION 6: ANALYSIS CONFIDENCE ===== */}
              <div className="intel-section-group">
                <h2 className="intel-section-header">Analysis Confidence</h2>
                <p className="intel-section-desc">How reliable is this analysis based on available data sources.</p>
                <ConfidencePanel data={intel.analysisConfidence ? { confidence: intel.analysisConfidence, confidenceLevel: intel.analysisConfidenceLevel || undefined } : null} />
              </div>

              {/* ===== SECTION 7: ADVANCED FORENSICS ===== */}
              <AdvancedForensics
                confidenceFactors={intel.eliteThreat}
                analysisStatus={intel.analysisStatus || undefined}
                meta={meta}
                cachedAt={d?.cachedAt}
              />
            </>
          )}

          {/* Compliance Protection Disclaimer */}
          <div className="ws-disclaimer" style={{ padding: '0.75rem 1rem', background: 'var(--surface-elevated, rgba(255,255,255,0.03))', borderRadius: '8px', marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <strong style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>Compliance Disclaimer</strong>
            {LEGAL_DISCLAIMER}
            {' '}Risk scores and threat levels are generated from automated on-chain analysis and third-party data providers. They do not constitute investment advice or endorsements, do not eliminate risk, and should not be interpreted as assurance. Always do your own research.
          </div>

          {/* Premium Lock Section — only shown for preview tier (not deep) */}
          {!isDeep && (
            <div className="ws-premium-lock">
              <div className="ws-lock-overlay" />
              <div className="ws-lock-content">
                <h3 className="ws-lock-title">Advanced Threat Intelligence</h3>
                {result?.teasers && (
                  <p className="ws-lock-teaser">{result.teasers.message}</p>
                )}
                <div className="ws-lock-grid">
                  {(result?.teasers?.lockedSections || LOCKED_SECTIONS).map((s: any) => (
                    <div key={s.id} className="ws-lock-card">
                      <span className="ws-lock-icon" aria-hidden="true">&#x1f512;</span>
                      <span className="ws-lock-label">{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className="ws-lock-actions">
                  <a href="/connect" className="ws-cta-btn">Connect Wallet to Unlock</a>
                  <a href="/pricing" className="ws-cta-secondary">View Pricing</a>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
